import { Palette } from 'lucide-react';
import { useAppStore } from '@/stores/app-store';
import { useSettingsStore } from '@/stores/settings-store';
import {
  EDITOR_THEMES,
  TERMINAL_THEMES,
  GRID_THEMES,
} from '@/config/themes';
import { useToastStore } from '@/stores/toast-store';
import type { Command } from '../types';

/**
 * Emit one command per theme so users can search e.g. `> dracula` and land
 * directly on the picker without hunting through the Settings modal.
 *
 * Each command is mode-aware — picking a dark theme while in light mode
 * also flips `appStore.theme` so the app actually switches. That's usually
 * what the user wanted when they typed the theme name.
 */

function setEditorTheme(id: string, mode: 'dark' | 'light'): void {
  const key = mode === 'dark' ? 'editor.themeDark' : 'editor.themeLight';
  useSettingsStore.getState().save({ [key]: id });
  if (useAppStore.getState().theme !== mode) useAppStore.getState().setTheme(mode);
  useToastStore.getState().addToast('success', `Editor theme: ${id}`);
}

function setTerminalTheme(id: string, mode: 'dark' | 'light'): void {
  const key = mode === 'dark' ? 'terminal.themeDark' : 'terminal.themeLight';
  useSettingsStore.getState().save({ [key]: id });
  if (useAppStore.getState().theme !== mode) useAppStore.getState().setTheme(mode);
  useToastStore.getState().addToast('success', `Terminal theme: ${id}`);
}

function setGridTheme(id: string, mode: 'dark' | 'light'): void {
  const key = mode === 'dark' ? 'grid.themeDark' : 'grid.themeLight';
  useSettingsStore.getState().save({ [key]: id });
  if (useAppStore.getState().theme !== mode) useAppStore.getState().setTheme(mode);
  useToastStore.getState().addToast('success', `Grid theme: ${id}`);
}

export const themeCommands: Command[] = [
  ...EDITOR_THEMES.map<Command>((t) => ({
    id: `theme.editor.${t.id}`,
    label: `Editor Theme: ${t.name}`,
    description: `${t.mode === 'dark' ? 'Dark' : 'Light'} mode`,
    icon: Palette,
    category: 'Theme',
    keywords: ['editor', 'monaco', t.mode],
    run: () => setEditorTheme(t.id, t.mode),
  })),
  ...TERMINAL_THEMES.map<Command>((t) => ({
    id: `theme.terminal.${t.id}`,
    label: `Terminal Theme: ${t.name}`,
    description: `${t.mode === 'dark' ? 'Dark' : 'Light'} mode`,
    icon: Palette,
    category: 'Theme',
    keywords: ['terminal', 'xterm', 'ssh', t.mode],
    run: () => setTerminalTheme(t.id, t.mode),
  })),
  ...GRID_THEMES.map<Command>((t) => ({
    id: `theme.grid.${t.id}`,
    label: `Data Grid Theme: ${t.name}`,
    description: `${t.mode === 'dark' ? 'Dark' : 'Light'} mode`,
    icon: Palette,
    category: 'Theme',
    keywords: ['grid', 'table', 'ag', t.mode],
    run: () => setGridTheme(t.id, t.mode),
  })),
];
